import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FetchPolicy } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { AssetGroup, AssetGroupInput } from '@app/core/model/entities/asset-group/asset-group';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, Observable, Subject } from 'rxjs';
import { map, reduce, tap } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class AssetGroupsService {

  private sidePanelToggleSubject = new Subject<AssetGroup | null>();

  private assetGroupAdded = new Subject<AssetGroup>();

  private assetGroupsUpdated = new Subject<AssetGroup[]>();
  private assetGroupsDeleted = new Subject<AssetGroup[]>();

  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private appConfig = inject(AppConfig);
  private router = inject(Router);
  private activationEndService = inject(ActivationEndService);

  private readonly assetGroupInfoGraphqlFragment = gql`
    fragment AssetGroupInfo on AssetGroup {
      id
      name
      identifier
      computedProperties
      properties
      organizationId
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
      dataDate
    }`;

  /**
   * Emits the new Asset Group after it was created.
   * @return Observable emitting the new Asset Group.
   */
  public get assetGroupAdded$(): Observable<AssetGroup> {
    return this.assetGroupAdded.asObservable();
  }

  /**
   * Emits the updated Asset Groups after they have been modified.
   * @return Observable emitting the updated Asset Groups.
   */
  public get assetGroupsUpdated$(): Observable<AssetGroup[]> {
    return this.assetGroupsUpdated.asObservable();
  }

  /**
   * Emits a list of Asset Groups after they have been deleted.
   * @return Observable emitting the deleted Asset Groups.
   */
  public get assetGroupsDeleted$(): Observable<AssetGroup[]> {
    return this.assetGroupsDeleted.asObservable();
  }

  /**
   * Emits an Asset Group whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<AssetGroup | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Open the sidenav with information about the selected Asset Group.
   * @param assetGroup Asset Group to display in the side panel.
   */
  public openAssetGroupSidePanel(assetGroup: AssetGroup): void {
    this.sidePanelToggleSubject.next(assetGroup);
  }

  /**
   * Close the side panel.
   */
  public closeAssetGroupSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Navigate to an Asset Group's sheet.
   * @param assetGroupId ID of the Asset Group to navigate to.
   * @param tabName Name of the tab to open after navigating to the Asset Group's sheet. Default tabName is features
   * @return Router's promise (return true if navigation succeeds)
   */
  public async navigateToAssetGroupSheet(assetGroupId: string, tabName = 'features'): Promise<void> {
    await this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'asset-groups',
      'asset-group-sheet',
      assetGroupId,
      tabName
    ]);
  }

  /**
   * Loads asset groups page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch asset groups based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the asset groups.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Asset Group objects, each with additional user information.
   */
  public loadAssetGroupsPage(selectParams: SelectParams): Observable<AssetGroup[]> {
    const query = gql`
      query AssetGroups($organizationId: String!, $selectParams: SelectParams!) {
        assetGroups(organizationId: $organizationId, selectParams: $selectParams) {
          ...AssetGroupInfo
        }
      }
      ${this.assetGroupInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(AssetGroup, data['assetGroups'] as AssetGroup[])));
  }

  /**
   * Loads all asset groups pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch asset groups based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Asset Group objects, each with additional user information.
   */
  public loadAllAssetGroups(selectParams: SelectParams): Observable<AssetGroup[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadAssetGroupsPage(params)
      .pipe(
        expand((assets, i) => {
          if (assets.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadAssetGroupsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as AssetGroup[])
      );
  }

  /**
   * Fetch an Asset Group by its ID
   * @param assetGroupId ID of the Asset Group to return.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Asset
   */
  public loadAssetGroup(assetGroupId: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<AssetGroup> {
    const query = gql`
      query AssetGroupById($assetGroupId: String!) {
        assetGroup(id: $assetGroupId) {
          ...AssetGroupInfo
        }
      }
      ${this.assetGroupInfoGraphqlFragment}
    `;
    const variables = {assetGroupId};
    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(AssetGroup, data['assetGroup'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Create a new Asset Group with the provided data.
   * @param assetGroupInput Asset Group data.
   * @return An Observable emitting the created AssetGroup.
   */
  public createAssetGroup(assetGroupInput: AssetGroupInput): Observable<AssetGroup> {
    const mutation = gql`
      mutation CreateAssetGroup($assetGroupInput: CreateAssetGroupInput!) {
        createAssetGroup(assetGroupInput: $assetGroupInput) {
          ...AssetGroupInfo
        }
      }
      ${this.assetGroupInfoGraphqlFragment}
    `;
    const variables = {
      assetGroupInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...assetGroupInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(AssetGroup, data['createAssetGroup'])),
        tap(newAssetGroup => this.assetGroupAdded.next(newAssetGroup))
      );
  }

  /**
   * Make a bulk API request to update a list of Asset Groups.
   * @param assetGroups List of objects containing assetGroup and assetGroupInput to update.
   * @return Updated Asset Groups.
   */
  public updateAssetGroups(assetGroups: {
    assetGroup: AssetGroup,
    assetGroupInput: AssetGroupInput
  }[]): Observable<AssetGroup[]> {
    const mutation = gql`
      mutation UpdateAssetGroups($assetGroupInputs: [UpdateAssetGroupInput!]!) {
        updateAssetGroups(assetGroupInputs: $assetGroupInputs) {
          ...AssetGroupInfo
        }
      }
      ${this.assetGroupInfoGraphqlFragment}
    `;
    const variables = {
      assetGroupInputs: assetGroups.map(({assetGroup, assetGroupInput}) => {
        return {
          id: assetGroup.id,
          name: assetGroup.name,
          ...assetGroupInput
        };
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(AssetGroup, data['updateAssetGroups'] as AssetGroup[])),
        tap(updatedAssetGroups => this.assetGroupsUpdated.next(updatedAssetGroups))
      );
  }

  /**
   * Make an API request to delete Asset Groups.
   * @param assetGroups AssetGroups to delete.
   * @return True if all Asset Groups have been deleted successfully, false otherwise.
   */
  public deleteAssetGroups(assetGroups: AssetGroup[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteAssetGroups($assetGroupIds: [String!]!) {
        deleteAssetGroups(ids: $assetGroupIds)
      }
    `;
    const variables = {assetGroupIds: assetGroups.map(assetGroup => assetGroup.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteAssetGroups'] as boolean),
        tap(success => success && this.assetGroupsDeleted.next(assetGroups))
      );
  }
}
